import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  inject,
  Input,
  type OnChanges,
  Output,
  signal,
  type SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { isNotBlank } from '@bizzdesign/bizzdesign-sdk';
import {
  defaultAttributeFilterValue,
  FilterGlobalStore,
  filterValueToAttributeDataPayload,
  getConditionOptions,
} from '@bizzdesign/data-access-search';
import { AttributeEditorComponent } from '@bizzdesign/feature-content-details';
import { SharedI18nModule } from '@bizzdesign/shared/i18n';
import {
  type AttributeDataPayload,
  type AttributeDropdownOption,
  type AttributeFilterValue,
  type AttributePayload,
  type BDTreeNode,
  type DropdownSettings,
} from '@bizzdesign/shared/models';
import {
  getBDCoreObjectTypeIcon,
  getSortedAttributeTreeNodeOptions,
} from '@bizzdesign/shared/utils';
import { DropdownComponent } from '@bizzdesign/ui-dropdown';
import { type TreeNode } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';

@Component({
  selector: 'bd-advanced-filter-attribute',
  imports: [
    NgClass,
    InputTextModule,
    SharedI18nModule,
    FormsModule,
    TooltipModule,
    ButtonModule,
    DividerModule,
    AttributeEditorComponent,
    DropdownComponent,
  ],
  templateUrl: './advanced-filter-attribute.component.html',
  styleUrls: ['./advanced-filter-attribute.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdvancedFilterAttributeComponent implements OnChanges {
  readonly filterGlobalStore = inject(FilterGlobalStore);

  @Input() filterValue: AttributeFilterValue = defaultAttributeFilterValue;

  @Output() submitAttributeFilter: EventEmitter<AttributeFilterValue> =
    new EventEmitter();
  @Output() removeAttributeFilter: EventEmitter<AttributeFilterValue> =
    new EventEmitter();

  attributeOptions = computed<BDTreeNode[]>(() =>
    getSortedAttributeTreeNodeOptions(
      this.filterGlobalStore.attributeCategoryDefinitions(),
    ),
  );

  conditionOptions = signal<BDTreeNode[]>([]);
  selectedAttributeOption: BDTreeNode | undefined;
  selectedConditionOption: BDTreeNode | undefined;
  attribute: AttributeDataPayload = {} as AttributeDataPayload;
  autoFocusConditionDropdown = signal(false);
  autoFocusAttributeEditor = signal(false);
  selectedAttributeOptionIcon = signal<string | undefined>(undefined);

  readonly attributeDropdownSettings = computed<DropdownSettings>(() => ({
    selectionMode: 'single',
    inputType: 'default',
    layout: 'tree',
    tScope: 'featureSearch',
    inputPlaceholderKey: 'attributeFilter.attributeDropdownPlaceholder',
    filterPlaceholderKey: 'attributeFilter.attributeDropdownSearchPlaceholder',
    filterOptionsCount: 0,
    dropdownLabelKey: 'attributeFilter.attributeDropdownLabel',
    icon: this.selectedAttributeOptionIcon(),
  }));

  readonly conditionDropdownSettings: DropdownSettings = {
    selectionMode: 'single',
    inputType: 'default',
    layout: 'list',
    tScope: 'featureSearch',
    inputPlaceholderKey: 'attributeFilter.conditionDropdownPlaceholder',
    dropdownWidth: '217px',
    dropdownLabelKey: 'attributeFilter.conditionDropdownLabel',
  };

  ngOnChanges(changes: SimpleChanges) {
    if (changes['filterValue']) {
      const filterValue = changes['filterValue']
        .currentValue as AttributeFilterValue;

      if (isNotBlank(filterValue.id)) {
        this.selectedAttributeOption = this.attributeOptions()
          .flatMap((elementTypeLevel) => elementTypeLevel.children)
          .flatMap((categoryLevel) => categoryLevel?.children)
          .find(
            (attribute) =>
              attribute?.data?.attributeId === filterValue.attribute.id &&
              attribute?.data?.categoryId === filterValue.attribute.categoryId,
          );

        this.conditionOptions.set(
          getConditionOptions(filterValue.attribute.type),
        );

        this.selectedConditionOption = this.conditionOptions().find(
          (attributeFilterOption) =>
            attributeFilterOption.data === filterValue.condition,
        );
      } else {
        this.selectedAttributeOption = undefined;
        this.selectedConditionOption = undefined;
      }
      this.setSelectedAttributeOptionIcon();
      this.updateAttributeData();
    }
  }

  setSelectedAttributeOptionIcon() {
    let elementTypeIcon: string | undefined;
    if (this.selectedAttributeOption?.data?.elementType) {
      elementTypeIcon = getBDCoreObjectTypeIcon(
        this.selectedAttributeOption.data.elementType,
      );
    }
    this.selectedAttributeOptionIcon.set(elementTypeIcon);
  }

  onAttributesChange(selectedAttributeOption?: TreeNode): void {
    this.selectedAttributeOption = selectedAttributeOption;
    this.selectedConditionOption = undefined;
    this.attribute = {} as AttributeDataPayload;

    if (selectedAttributeOption) {
      const data = selectedAttributeOption.data as AttributeDropdownOption;
      const categoryData = selectedAttributeOption.parent!;

      const conditionOptions = getConditionOptions(data.attributeType);
      this.conditionOptions.set(conditionOptions);

      this.filterValue = {
        id: this.filterValue.id ?? '',
        attribute: {
          id: data.attributeId,
          name: data.attributeName,
          type: data.attributeType,
          categoryId: data.categoryId,
          categoryName: categoryData.label!,
        } as AttributePayload,
        condition: '',
        referencedObjectName: '',
        elementType: data.elementType,
      };

      this.autoFocusConditionDropdown.set(true);
      this.autoFocusAttributeEditor.set(false);
      if (this.filterValue.id !== '') {
        this.submitFilterValue();
      }
    } else {
      this.removeAttributeFilterValue();
    }
    this.setSelectedAttributeOptionIcon();
    this.updateAttributeData();
  }

  onConditionChange(selectedConditionOption?: TreeNode): void {
    this.selectedConditionOption = selectedConditionOption;
    this.filterValue = {
      ...this.filterValue,
      condition: selectedConditionOption?.key ?? '',
    };
    const isExistenceOperator =
      selectedConditionOption?.key === 'exists' ||
      selectedConditionOption?.key === 'not_exists';
    if (selectedConditionOption && !isExistenceOperator) {
      this.autoFocusConditionDropdown.set(false);
      this.autoFocusAttributeEditor.set(true);
    } else {
      //If the condition is unselected or is an exists condition, we reset the attribute value
      this.filterValue = {
        ...this.filterValue,
        attribute: {
          ...this.filterValue.attribute,
          value: undefined,
        },
      };
      this.updateAttributeData();
      this.autoFocusAttributeEditor.set(false);
    }

    if (this.filterValue.id !== '' || isExistenceOperator) {
      this.submitFilterValue();
    }
  }

  removeAttributeFilterValue() {
    this.removeAttributeFilter.emit(this.filterValue);
    this.filterValue = { ...defaultAttributeFilterValue };
    this.selectedAttributeOption = undefined;
    this.selectedConditionOption = undefined;
    this.autoFocusConditionDropdown.set(false);
    this.autoFocusAttributeEditor.set(false);
    this.setSelectedAttributeOptionIcon();
  }

  submitFilterValue() {
    this.submitAttributeFilter.emit(this.filterValue);
  }

  updateAttributeData() {
    this.attribute = filterValueToAttributeDataPayload(
      this.filterGlobalStore.attributeCategoryDefinitions(),
      this.filterValue,
      this.selectedAttributeOption
        ? (this.selectedAttributeOption.data as AttributeDropdownOption)
            .elementType
        : '',
    );
  }

  onUpdateAttributeValue(attribute: AttributeDataPayload) {
    this.filterValue = {
      ...this.filterValue,
      attribute: {
        ...this.filterValue.attribute,
        value: attribute.value,
      } as AttributeFilterValue['attribute'],
      referencedObjectName: attribute.objectName,
      referencedObjectSystemAttributes: attribute.systemAttributes,
    };
    this.submitFilterValue();
    this.updateAttributeData();
    this.autoFocusConditionDropdown.set(false);
    this.autoFocusAttributeEditor.set(false);
  }
}
